/** @jsx jsx */

import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import ie11VerticalFlexCenterFix from 'services/ie11VerticalFlexCenterFix';
import getStore from 'services/getStore';
import pubSub from 'services/pubSub';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import getBlockById from 'services/apiHelpers/getBlockById';
import { useSelector, useDispatch } from 'react-redux';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import updateReadOnlyText from 'services/updateReadOnlyText';
import { motion } from 'framer-motion';
import { InteractionContext } from 'components/Layouts/ContainerLogical';
import { killAllAudio } from 'services/killAllAudio';
import { isIOSSafari } from 'services/isIOSSafari';

const store = getStore();

let container;
let modalOpen = false;

interface IModal {
  useFader?: boolean;
  disableNullSpaceClick?: boolean;
  minWidth?: string;
  onClose?: () => void;
  onOpened?: () => void;
  children: any;
}

const Modal = ({
  useFader = true,
  disableNullSpaceClick = false,
  minWidth = '50vw',
  onClose,
  onOpened = () => {},
  children,
}: IModal) => {
  if (!container) container = document.querySelector('#modal-container');

  const interactionContext = useContext(InteractionContext);

  const [btnColor, setBtnColor] = useState<string | undefined>(undefined);

  const focusedNodeIdOnOpen = useSelector<any>(
    ({ focusedNodeId }) => focusedNodeId
  );

  const rootContainer = useRef<HTMLDivElement>(null);
  const contentContainer = useRef<HTMLDivElement | null>(null);
  const hiddenButton = useRef(null);
  const buttonTabIndex = useRef(0);
  const checkResize = useRef(false);

  const getButtonAsAnchorOrButton = (
    container: HTMLElement
  ): HTMLAnchorElement | HTMLButtonElement | null => {
    if (!container) {
      return null;
    }

    return (
      container.querySelector('[class*="navigationButton"]') ||
      container.querySelector('[class*="genericButton"]') ||
      null
    );
  };

  useEffect(() => {
    modalOpen = true;
    window['MODAL_OPEN'] = true;

    contentContainer.current!.focus();

    const content: any = contentContainer.current!.querySelector(
      '.chameleon-text'
    );

    if (content) {
      content.setAttribute('tabindex', '0');
      content.focus();
    }

    //
    let button =
      contentContainer.current &&
      getButtonAsAnchorOrButton(contentContainer.current);

    if (button && !button.hasAttribute('data-custom-label')) {
      button?.setAttribute('aria-label', 'Close dialog');
    }

    window.addEventListener('keydown', tabOverride);

    // give ALL other elements aria-hidden ???
    const root = document.querySelector('#root')!;
    root.setAttribute('aria-hidden', 'true');

    document.body.style.overflowY = 'hidden';

    const onResize = () => {
      if (!rootContainer.current) return;

      rootContainer.current.style.alignItems =
        window.innerHeight < contentContainer.current!.scrollHeight
          ? 'flex-start'
          : 'center';

      checkResize.current = true;
    };

    const tick = setInterval(() => {
      onResize();
      window.addEventListener('resize', onResize);
      if (checkResize.current) clearInterval(tick);
    }, 100);

    function tabOverride(e) {
      const content: any = contentContainer.current?.querySelector(
        '.chameleon-text'
      );

      // this is to color the focus of the hidden button
      // doesn't really feel valuable at all but... is already in play
      button =
        contentContainer.current &&
        getButtonAsAnchorOrButton(contentContainer.current);

      if (button) {
        const buttonBackgroundColor = window.getComputedStyle(button)
          .backgroundColor;

        if (!btnColor) {
          setBtnColor(buttonBackgroundColor);
        }
      }

      if (['Tab'].includes(e.key)) {
        //
        let reverseTab;
        if (e.shiftKey) {
          reverseTab = true;
        }

        e.preventDefault();

        // elements will contain all the things we're tabbing through
        const elements: any = [];

        if (content) {
          elements.push(content);

          // capture any links in the content
          const links = Array.from(content.querySelectorAll('a'));
          // and push 'em into the elements list
          if (links && links.length) {
            links.forEach(link => {
              elements.push(link);
            });
          }
        }

        // grab whatever buttons we have
        // taking into account that class*="navigationButton is actually an anchor element
        const buttons = contentContainer.current?.querySelectorAll(
          '[class*="navigationButton"], [class*="genericButton"], button'
        );

        if (buttons && buttons.length) {
          buttons.forEach(button => {
            elements.push(button);
          });
        }

        // push the hiddenButton in last
        elements.push(hiddenButton.current);

        elements[
          (buttonTabIndex.current = reverseTab
            ? (buttonTabIndex.current - 1 + elements.length) % elements.length
            : (buttonTabIndex.current + 1) % elements.length)
        ].focus();
      }
    }

    return () => {
      modalOpen = false;
      window['MODAL_OPEN'] = false;

      root.setAttribute('aria-hidden', 'false');
      document.body.style.overflowY = 'auto';

      checkResize.current = false;
      window.removeEventListener('keydown', tabOverride);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!window.dev) {
      // we're opening the modal from the editor so we don't need these checks
      return;
    }

    pubSub.subscribe('CLOSE_MODAL', $ => {
      onClose!();
    });

    return () => {
      const { focusedNodeId } = store.getState();

      if (!focusedNodeId) {
        return;
      }

      const block = getBlockById(focusedNodeId);

      const isNotInteraction = [
        'text',
        'image',
        'video',
        'svg',
        'embed',
        'editablebutton',
        'continuebutton',
      ].includes(block.type.toLowerCase());

      if (isNotInteraction) {
        if (focusedNodeIdOnOpen) {
          setCurrentBlock({ id: focusedNodeIdOnOpen });
        } else {
          setCurrentBlock(null);
        }
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (window.dev && !window.dev()) {
      updateReadOnlyText(contentContainer.current);
    }
  }, []);

  useEffect(() => {
    if (window.dev && window.dev()) {
      return;
    }

    // find all the hrefs a

    const links = Array.from(contentContainer.current!.querySelectorAll('a'));

    if (!links.length) {
      return;
    }

    const closeOnLinkClick = e => {
      onClose!();
    };

    const addRemoveCloseOnLinkClick = addOrRemove => {
      links.forEach(link => {
        if (link.href.includes('#')) {
          link[`${addOrRemove}EventListener`]('click', closeOnLinkClick);
        }
      });
    };

    addRemoveCloseOnLinkClick('add');

    return () => {
      addRemoveCloseOnLinkClick('remove');
    };
  }, []);

  useEffect(() => {
    // ' proof of concept '

    if (interactionContext?.stopAudioWhenModalIsOpened) {
      killAllAudio();
    }

    //
  }, []);

  return createPortal(
    <motion.div
      ref={rootContainer}
      initial={{ opacity: 0 }}
      transition={{
        duration: useFader ? 0.325 : 0,
        ease: 'easeOut',
      }}
      animate={{ opacity: 1 }}
      onAnimationComplete={onOpened}
      css={css`
        position: fixed;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100vw;
        min-height: 100vh;
        max-height: 100%;
        padding: ${isIOSSafari() ? '65px' : '10px'} 10px;
        background: rgba(0, 0, 0, 0.6);
        z-index: 1;
        ${ie11VerticalFlexCenterFix('height: 100vh;')}

        overflow-y: scroll;
      `}
    >
      <button
        ref={hiddenButton}
        css={css`
          position: absolute;
          left: 3px;
          top: 3px;
          width: calc(100% - 6px);
          height: calc(100% - 6px);

          border: none;
          outline: none;
          background: transparent;

          &:focus {
            border: 1px solid ${btnColor || '#282a2b'};
          }

          ${disableNullSpaceClick &&
            `
              pointer-events: none;
          `}
        `}
        onClick={(e: any) => {
          if (disableNullSpaceClick) {
            return;
          }
          //
          onClose!();
        }}
        aria-hidden="true"
      ></button>

      <div
        ref={contentContainer}
        css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-width: 1024px;
          border-radius: 50px;
          background: #fff;
          box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.2);
        `}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </motion.div>,
    container
  );
};

const useModal = ({ children }) => {
  const [, setState] = useState(true);

  return <Modal onClose={() => setState(false)}>{children}</Modal>;
};

export default Modal;

export { useModal };
